import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { useModal } from '../context/ModalContext';
import { useContent } from '../context/ContentContext';
import { ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import { getMenuConfig } from '../lib/menuConfig';

const DEFAULT_HERO_BADGE = {
  number: '37+',
  label_en: 'Years of Excellence',
  label_ar: 'عاماً من التميز',
  enabled: true,
};

const HeroSection = () => {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const { openModal } = useModal();
  const { siteContent } = useContent();
  const isRtl = language === 'ar';
  const [currentSlide, setCurrentSlide] = useState(0);
  const menuConfig = getMenuConfig(siteContent?.global?.menu);

  const rawSlides = siteContent?.hero?.slides || [];
  const slides = rawSlides.filter(s => s.enabled !== false).sort((a, b) => (a.order || 0) - (b.order || 0));
  const autoplayInterval = siteContent?.hero?.autoplay_interval_ms || 6000;
  const heroBadge = { ...DEFAULT_HERO_BADGE, ...(siteContent?.hero?.badge || {}) };
  const badgeLabel = language === 'ar' ? heroBadge.label_ar : heroBadge.label_en;
  const isSectionEnabled = (key) => menuConfig[key]?.enabled !== false;

  useEffect(() => {
    if (slides.length < 2) return;
    const timer = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % slides.length);
    }, autoplayInterval);
    return () => clearInterval(timer);
  }, [slides.length, autoplayInterval]);

  const nextSlide = () => setCurrentSlide(prev => (prev + 1) % slides.length);
  const prevSlide = () => setCurrentSlide(prev => (prev - 1 + slides.length) % slides.length);

  const scrollToAbout = () => {
    if (!scrollTarget) return;
    const el = document.querySelector(`#${scrollTarget}`);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  if (slides.length === 0) return null;

  const slide = slides[currentSlide];
  const overline = language === 'ar' ? slide.overline_ar : slide.overline_en;
  const titleHighlight = language === 'ar' ? slide.title_highlight_ar : slide.title_highlight_en;
  const titleMain = language === 'ar' ? slide.title_main_ar : slide.title_main_en;
  const description = language === 'ar' ? slide.description_ar : slide.description_en;
  const cta = language === 'ar' ? slide.cta_ar : slide.cta_en;
  const PrevIcon = isRtl ? ChevronRight : ChevronLeft;
  const NextIcon = isRtl ? ChevronLeft : ChevronRight;

  const openSlideModal = () => {
    const target = slide.cta_target || 'about';
    if (isSectionEnabled(target)) {
      openModal('section', { section: target });
    }
  };

  const scrollTarget = ['about', 'products', 'news', 'contact'].find(isSectionEnabled);

  return (
    <section
      id="home"
      data-testid="hero-section"
      className={`relative min-h-screen pt-28 sm:pt-32 overflow-hidden ${isDark ? 'bg-[#050505]' : 'bg-[#FDFBF7]'}`}
    >
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23D4AF37' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")` }} />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 min-h-[calc(100vh-8rem)] flex items-center">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center w-full py-12">
          <div className={`order-2 ${isRtl ? 'lg:order-2 text-right' : 'lg:order-1 text-left'}`}>
            <p className="text-[var(--gold-primary)] uppercase tracking-[0.2em] text-xs sm:text-sm font-semibold mb-4" data-testid="hero-overline">
              {overline}
            </p>
            <h1 className={`font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold mb-6 ${isRtl ? 'leading-[1.6] tracking-normal' : 'leading-[1.1] tracking-tight'}`} data-testid="hero-title">
              <span className={`block ${isDark ? 'text-white/90' : 'text-gray-800'}`}>{titleHighlight}</span>
              <span className="gold-text block">{titleMain}</span>
            </h1>
            <p className={`text-base sm:text-lg lg:text-xl mb-8 leading-relaxed max-w-xl ${isDark ? 'text-white/70' : 'text-gray-600'} ${isRtl ? 'mr-0 ml-auto' : ''}`} data-testid="hero-subtitle">
              {description}
            </p>
            {(isSectionEnabled(slide.cta_target || 'about') || isSectionEnabled('products')) && (
              <div className={`flex items-center gap-4 flex-wrap ${isRtl ? 'justify-end flex-row-reverse' : ''}`}>
                {isSectionEnabled(slide.cta_target || 'about') && (
                  <button
                    onClick={openSlideModal}
                    className="btn-shine inline-flex items-center gap-3 px-8 py-4 bg-[var(--gold-primary)] text-black font-semibold text-sm uppercase tracking-wider rounded-sm hover:bg-[var(--gold-hover)] transition-all duration-300"
                    data-testid="hero-cta"
                  >
                    {cta}
                  </button>
                )}
                {isSectionEnabled('products') && (
                  <button
                    onClick={() => openModal('section', { section: 'products' })}
                    className={`inline-flex items-center gap-2 px-6 py-4 border-2 border-[var(--gold-primary)] font-semibold text-sm uppercase tracking-wider rounded-sm transition-all duration-300 text-[var(--gold-primary)] hover:bg-[var(--gold-primary)] hover:text-black ${isRtl ? 'flex-row-reverse' : ''}`}
                    data-testid="hero-secondary-cta"
                  >
                    {language === 'ar' ? 'منتجاتنا' : 'Our Products'}
                  </button>
                )}
              </div>
            )}
            <div className={`flex items-center gap-3 mt-12 ${isRtl ? 'justify-end' : 'justify-start'}`}>
              {slides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`h-1 rounded-full transition-all duration-300 ${currentSlide === index ? 'w-10 bg-[var(--gold-primary)]' : `w-3 ${isDark ? 'bg-white/30 hover:bg-white/50' : 'bg-gray-300 hover:bg-gray-400'}`}`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>

          <div className={`order-1 ${isRtl ? 'lg:order-1' : 'lg:order-2'} relative`}>
            <div className="relative">
              <div className={`absolute -inset-4 border-2 ${isDark ? 'border-[var(--gold-primary)]/20' : 'border-[var(--gold-primary)]/30'} rounded-sm`} />
              <div className="relative aspect-[4/3] sm:aspect-square lg:aspect-[4/5] overflow-hidden rounded-sm">
                <div className="absolute inset-0 bg-cover bg-center transition-transform duration-700" style={{ backgroundImage: `url('${slide.image_url}')`, transform: 'scale(1.05)' }} />
                <div className={`absolute inset-0 ${isDark ? 'bg-black/30' : 'bg-black/10'}`} />
                <div className={`absolute bottom-6 ${isRtl ? 'left-6' : 'right-6'} bg-black/60 backdrop-blur-sm p-4 rounded-sm`}>
                  <img
                    src={siteContent?.global?.logo_url_dark || '/uploads/f0ff146b-d9ae-4c6c-b8cb-08a79738ee50.png'}
                    alt="Royal Tobacco Emblem"
                    className="h-16 w-auto"
                  />
                </div>
              </div>
              {slides.length > 1 && (
                <div className={`absolute top-1/2 -translate-y-1/2 left-0 right-0 flex justify-between px-4 pointer-events-none ${isRtl ? 'flex-row-reverse' : ''}`}>
                  <button onClick={prevSlide} className={`pointer-events-auto w-10 h-10 flex items-center justify-center rounded-full transition-all duration-300 ${isDark ? 'bg-black/50 hover:bg-[var(--gold-primary)] text-white hover:text-black' : 'bg-white/80 hover:bg-[var(--gold-primary)] text-gray-800 hover:text-black'}`} aria-label="Previous slide"><PrevIcon className="w-5 h-5" /></button>
                  <button onClick={nextSlide} className={`pointer-events-auto w-10 h-10 flex items-center justify-center rounded-full transition-all duration-300 ${isDark ? 'bg-black/50 hover:bg-[var(--gold-primary)] text-white hover:text-black' : 'bg-white/80 hover:bg-[var(--gold-primary)] text-gray-800 hover:text-black'}`} aria-label="Next slide"><NextIcon className="w-5 h-5" /></button>
                </div>
              )}
            </div>
            {heroBadge.enabled !== false && (
              <div className={`absolute -bottom-6 ${isRtl ? '-left-4 sm:-left-8' : '-right-4 sm:-right-8'} p-4 sm:p-6 rounded-sm shadow-xl ${isDark ? 'bg-[#111111] border border-[var(--border-color)]' : 'bg-white border border-gray-200'}`}>
                <p className="font-display text-3xl sm:text-4xl font-bold gold-text" dir="ltr">{heroBadge.number}</p>
                <p className={`text-xs sm:text-sm uppercase tracking-wider ${isDark ? 'text-white/60' : 'text-gray-500'}`}>{badgeLabel}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {scrollTarget && (
        <button onClick={scrollToAbout} className={`absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 cursor-pointer group ${isDark ? 'text-white/50' : 'text-gray-400'}`} data-testid="scroll-indicator" aria-label="Scroll to About">
          <span className="text-xs uppercase tracking-widest group-hover:text-[var(--gold-primary)] transition-colors">{language === 'ar' ? 'اكتشف المزيد' : 'Scroll Down'}</span>
          <ChevronDown className="w-5 h-5 animate-bounce text-[var(--gold-primary)]" />
        </button>
      )}
    </section>
  );
};

export default HeroSection;
